import { createContext, useContext } from "react";
import type { TenantConfig } from "./types";
import { defaultTenant } from "./default";
import { kchTenant } from "./kch";
import { bosongTenant } from "./bosong";
import { zyTenant } from "./zy";
import { huayuTenant } from "./huayu";
import { librademoTenant } from "./librademo";
import { libraosTenant } from "./libraos";

const TENANTS: Record<string, TenantConfig> = {
  default: defaultTenant,
  kch: kchTenant,
  bosong: bosongTenant,
  zy: zyTenant,
  huayu: huayuTenant,
  librademo: librademoTenant,
  libraos: libraosTenant,
};

interface RuntimeConfig {
  tenant?: string;
}

declare global {
  interface Window {
    __SCHOOL_UI_TENANT__?: RuntimeConfig;
  }
}

/**
 * Tenant resolution order:
 * 1. `window.__SCHOOL_UI_TENANT__.tenant` — set by the production
 *    container's startup script that writes /config.json based on
 *    the VITE_TENANT env var. Loaded by `loadRuntimeConfig` before
 *    React mounts (see main.tsx).
 * 2. `import.meta.env.VITE_TENANT` — dev fallback. `VITE_TENANT=kch
 *    npm run dev` works without any container.
 * 3. `default` — used when nothing matches.
 */
export function resolveTenant(): TenantConfig {
  const fromWindow =
    typeof window !== "undefined" ? window.__SCHOOL_UI_TENANT__?.tenant : undefined;
  const fromEnv = import.meta.env?.VITE_TENANT as string | undefined;
  const slug = fromWindow ?? fromEnv ?? "default";
  return TENANTS[slug] ?? defaultTenant;
}

/**
 * Fetch /config.json (written by the container at start) and stash
 * the result on `window.__SCHOOL_UI_TENANT__` so `resolveTenant()`
 * picks it up synchronously after this resolves. Idempotent: if the
 * window already has a value, returns immediately.
 *
 * Called once from main.tsx BEFORE createRoot so the first render
 * sees the right tenant. 404 is fine (dev mode without nginx-served
 * config) — we just fall through to env / default.
 */
export async function loadRuntimeConfig(): Promise<void> {
  if (typeof window === "undefined") return;
  if (window.__SCHOOL_UI_TENANT__) return;
  try {
    const r = await fetch("/config.json", { cache: "no-store" });
    if (!r.ok) return;
    const cfg = (await r.json()) as RuntimeConfig;
    window.__SCHOOL_UI_TENANT__ = cfg;
  } catch {
    // best-effort; fall back to env / default
  }
}

const TenantContext = createContext<TenantConfig>(defaultTenant);
export const TenantProvider = TenantContext.Provider;

export function useTenant(): TenantConfig {
  return useContext(TenantContext);
}

// Re-exports so callers can `import { TenantConfig } from "@/tenants"`.
export type { TenantConfig } from "./types";

/** Locales the current tenant offers (ordered as they should appear). */
export function tenantLocales(tenant: TenantConfig): NonNullable<TenantConfig["locales"]> {
  return tenant.locales && tenant.locales.length > 0 ? tenant.locales : ["en", "zh-CN"];
}
